import React, { useState } from "react";
import { doc, updateDoc } from "firebase/firestore";
import { db } from "../../firebase";
import { useToast } from "../../components/ui/ToastProvider";
import { useConfirm } from "../../components/ui/ConfirmProvider";
import { mapFirebaseError } from "../../utils/userErrors";

export default function PresetTimeCard({ familyId, familySettings, canManage }) {
  const { showToast } = useToast();
  const { confirm } = useConfirm();
  const [name, setName] = useState("");
  const [time, setTime] = useState("");
  const [errorText, setErrorText] = useState("");
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState("");
  const presets = familySettings?.presetTimes || [];
  const isPro = familySettings?.isPro || false;

  // PRO ONLY GUARD
  if (!isPro) return null;

  const addPreset = async () => {
    if (!canManage) return;
    if (!name.trim()) {
      setErrorText("Preset name is required.");
      return;
    }
    if (!time) {
      setErrorText("Preset time is required.");
      return;
    }
    setErrorText("");
    setSaving(true);
    const newPresets = [...presets, { id: Date.now().toString(), name, time }];
    try {
      await updateDoc(doc(db, "families", familyId), {
        presetTimes: newPresets
      });
      setName("");
      setTime("");
      showToast({ type: "success", message: "Preset added." });
    } catch (error) {
      setErrorText(mapFirebaseError(error, "Unable to add preset."));
    } finally {
      setSaving(false);
    }
  };

  const deletePreset = async (id) => {
    if (!canManage) return;
    const preset = presets.find((entry) => entry.id === id);
    const approved = await confirm({
      title: "Delete preset?",
      message: `Delete "${preset?.name || "this preset"}"?`,
      confirmLabel: "Delete",
      cancelLabel: "Cancel",
      danger: true
    });
    if (!approved) return;
    setDeletingId(id);
    const filtered = presets.filter(p => p.id !== id);
    try {
      await updateDoc(doc(db, "families", familyId), {
        presetTimes: filtered
      });
      showToast({ message: "Preset deleted." });
    } catch (error) {
      setErrorText(mapFirebaseError(error, "Unable to delete preset."));
    } finally {
      setDeletingId("");
    }
  };

  return (
    <div className="glass parent-card">
      <div className="header-flex">
        <h2>Chore Time Presets</h2>
      </div>
      <div className="card-body">
        {!canManage && (
          <p style={{ textAlign: "center", opacity: 0.6 }}>You have view-only access.</p>
        )}
        {errorText && (
          <small className="pin-error" role="alert">
            {errorText}
          </small>
        )}
        <div style={{ display: 'flex', gap: '8px', marginBottom: '15px' }}>
          <input 
            placeholder="Name (e.g. Bedtime)" 
            value={name} 
            onChange={e => {
              setName(e.target.value);
              if (errorText) setErrorText("");
            }} 
            disabled={!canManage}
          />
          <input 
            type="time" 
            value={time} 
            onChange={e => {
              setTime(e.target.value);
              if (errorText) setErrorText("");
            }} 
            disabled={!canManage}
          />
          <button
            className="btn-nav success-btn"
            onClick={addPreset}
            disabled={!canManage || saving}
            aria-label="Add preset time"
            title="Add preset time"
          >
            {saving ? "..." : "+"}
          </button>
        </div>

        {presets.map(p => (
          <div key={p.id} className="parent-list-item" style={{ background: 'rgba(255,255,255,0.3)', padding: '8px 12px', borderRadius: '8px', marginBottom: '5px' }}>
            <span><strong>{p.name}</strong> ({p.time})</span>
            <button
              className="btn-nav"
              onClick={() => deletePreset(p.id)}
              style={{ padding: '4px 10px', color: 'var(--danger)' }}
              disabled={!canManage || deletingId === p.id}
              aria-label={`Delete preset ${p.name}`}
              title={`Delete preset ${p.name}`}
            >
              {deletingId === p.id ? "..." : "×"}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
